/**
 * WCAG Accessibility Fixes — Private Label NYC
 * Theme: Broadcast v5.3.0
 * June 2026
 *
 * Fixes applied:
 *  1. Keyboard navigation for nav dropdowns (data-nav-keyboard-toggle buttons)
 *  2. Cart drawer focus trap (Broadcast theme cart drawer)
 *  3. Emoji hidden from screen readers in nav/headings
 *  4. "Read more" sr-only text (backup JS in case Liquid output needs supplement)
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     1. KEYBOARD NAV FOR DROPDOWNS
     WCAG 2.1.1 / 4.1.2 (Level A)

     The Broadcast theme uses data-hover-disclosure-toggle for mouse hover.
     We added hidden <button data-nav-keyboard-toggle> in nav-item.liquid.
     This script:
     - Shows those toggle buttons only for keyboard users
     - Wires Enter/Space to open/close
     - Wires Escape to close + return focus
     - Wires Tab-out to close
     ───────────────────────────────────────────── */
  function initKeyboardNav() {
    var toggleButtons = document.querySelectorAll('[data-nav-keyboard-toggle]');

    if (!toggleButtons.length) return;

    // Remove visually-hidden from toggle buttons when user is keyboard-navigating
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Tab') {
        toggleButtons.forEach(function (btn) {
          btn.classList.remove('visually-hidden');
          btn.style.position = 'static';
          btn.style.width = 'auto';
          btn.style.height = 'auto';
          btn.style.clip = 'auto';
          btn.style.overflow = 'visible';
          btn.style.margin = '0';
        });
      }
    });

    toggleButtons.forEach(function (btn) {
      var targetId = btn.getAttribute('aria-controls');
      var dropdown = targetId ? document.getElementById(targetId) : null;
      var menuItem = btn.closest('.menu__item');

      if (!dropdown) return;

      function openDropdown() {
        btn.setAttribute('aria-expanded', 'true');
        // Trigger the theme's existing disclosure system too
        var themeToggle = menuItem ? menuItem.querySelector('[data-hover-disclosure-toggle]') : null;
        if (themeToggle) {
          themeToggle.setAttribute('aria-expanded', 'true');
          menuItem.classList.add('is-focused');
        }
        // Move focus to first menu item
        requestAnimationFrame(function () {
          var firstItem = dropdown.querySelector('a[role="menuitem"], [role="menuitem"]');
          if (firstItem) firstItem.focus();
        });
      }

      function closeDropdown() {
        btn.setAttribute('aria-expanded', 'false');
        var themeToggle = menuItem ? menuItem.querySelector('[data-hover-disclosure-toggle]') : null;
        if (themeToggle) {
          themeToggle.setAttribute('aria-expanded', 'false');
          menuItem.classList.remove('is-focused');
        }
        btn.focus();
      }

      btn.addEventListener('click', function () {
        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          closeDropdown();
        } else {
          // Close all other open dropdowns first
          toggleButtons.forEach(function (other) {
            if (other !== btn) {
              other.setAttribute('aria-expanded', 'false');
            }
          });
          openDropdown();
        }
      });

      // Escape from within dropdown
      dropdown.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          closeDropdown();
        }
      });

      // Close when Tab leaves the menu item
      if (menuItem) {
        menuItem.addEventListener('focusout', function (e) {
          if (!menuItem.contains(e.relatedTarget)) {
            btn.setAttribute('aria-expanded', 'false');
          }
        });
      }
    });

    // Global Escape closes all
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        toggleButtons.forEach(function (btn) {
          btn.setAttribute('aria-expanded', 'false');
        });
      }
    });
  }


  /* ─────────────────────────────────────────────
     2. CART DRAWER FOCUS TRAP
     WCAG 2.4.3 (Level A)

     Broadcast theme uses .drawer--cart and dispatches
     'theme:cart:open' / 'theme:cart:close' events.
     ───────────────────────────────────────────── */
  function initCartFocusTrap() {
    var cartDrawer = document.querySelector('.drawer--cart, #cart-drawer, [data-cart-drawer]');
    if (!cartDrawer) return;

    var lastFocusedElement = null;

    var FOCUSABLE_SELECTORS = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    function getFocusableEls() {
      return Array.from(cartDrawer.querySelectorAll(FOCUSABLE_SELECTORS)).filter(function (el) {
        return !el.closest('[hidden]') && el.offsetParent !== null;
      });
    }

    function trapTab(e) {
      if (e.key !== 'Tab') return;
      var focusable = getFocusableEls();
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function onCartOpen() {
      lastFocusedElement = document.activeElement;
      cartDrawer.setAttribute('role', 'dialog');
      cartDrawer.setAttribute('aria-modal', 'true');
      if (!cartDrawer.getAttribute('aria-label')) {
        cartDrawer.setAttribute('aria-label', 'Shopping cart');
      }
      document.addEventListener('keydown', trapTab);
      requestAnimationFrame(function () {
        var focusable = getFocusableEls();
        if (focusable.length) focusable[0].focus();
      });
    }

    function onCartClose() {
      document.removeEventListener('keydown', trapTab);
      if (lastFocusedElement) {
        lastFocusedElement.focus();
        lastFocusedElement = null;
      }
    }

    // Broadcast theme events
    document.addEventListener('theme:cart:open', onCartOpen);
    document.addEventListener('theme:cart:close', onCartClose);

    // Also watch class changes as fallback (theme adds/removes 'is-visible')
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName === 'class') {
          var isOpen = cartDrawer.classList.contains('is-visible') ||
                       cartDrawer.classList.contains('drawer--open');
          if (isOpen && lastFocusedElement === null) {
            onCartOpen();
          } else if (!isOpen && lastFocusedElement !== null) {
            onCartClose();
          }
        }
      });
    });
    observer.observe(cartDrawer, { attributes: true });

    // Escape closes drawer
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var closeBtn = cartDrawer.querySelector('[data-cart-close], .drawer__close, [aria-label*="close" i], [aria-label*="Close" i]');
        if (closeBtn && (cartDrawer.classList.contains('is-visible') || cartDrawer.classList.contains('drawer--open'))) {
          closeBtn.click();
        }
      }
    });
  }


  /* ─────────────────────────────────────────────
     3. HIDE EMOJI FROM SCREEN READERS
     WCAG 1.1.1 Best Practice

     Targets nav links and headings — not body copy.
     Wraps emoji in aria-hidden spans.
     ───────────────────────────────────────────── */
  function hideNavEmoji() {
    var targets = document.querySelectorAll(
      '.navlink, .navtext, header a, nav a, h1, h2, h3, .footer a'
    );

    // Emoji regex — covers most common emoji ranges
    var emojiRegex = /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;

    targets.forEach(function (el) {
      // Only process direct text nodes
      var childNodes = Array.from(el.childNodes);
      childNodes.forEach(function (node) {
        if (node.nodeType !== Node.TEXT_NODE) return;
        if (!emojiRegex.test(node.textContent)) return;
        emojiRegex.lastIndex = 0;

        var text = node.textContent;
        var newHtml = text.replace(emojiRegex, function (match) {
          return '<span aria-hidden="true">' + match + '</span>';
        });

        if (newHtml !== text) {
          var span = document.createElement('span');
          span.innerHTML = newHtml;
          var frag = document.createDocumentFragment();
          while (span.firstChild) {
            frag.appendChild(span.firstChild);
          }
          node.parentNode.replaceChild(frag, node);
        }
      });
    });
  }


  /* ─────────────────────────────────────────────
     4. SEARCH INPUT LABEL
     WCAG 1.3.1 (Level A)

     Broadcast theme search input may lack a proper label.
     This adds one if missing.
     ───────────────────────────────────────────── */
  function fixSearchLabel() {
    var searchInputs = document.querySelectorAll(
      'input[type="search"], input[name="q"], .header-search__input, [data-predictive-search-input]'
    );

    searchInputs.forEach(function (input) {
      if (input.getAttribute('aria-label') || input.id && document.querySelector('label[for="' + input.id + '"]')) {
        return; // already labelled
      }
      input.setAttribute('aria-label', 'Search products');
      if (!input.getAttribute('placeholder')) {
        input.setAttribute('placeholder', 'Search products');
      }
    });
  }


  /* ─────────────────────────────────────────────
     INIT
     ───────────────────────────────────────────── */
  function init() {
    initKeyboardNav();
    initCartFocusTrap();
    hideNavEmoji();
    fixSearchLabel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run emoji fix after Shopify Section Rendering (dynamic sections)
  document.addEventListener('shopify:section:load', function () {
    hideNavEmoji();
    fixSearchLabel();
  });

})();

/* ─────────────────────────────────────────────
   5. DUPLICATE NAV aria-hidden TOGGLE
   WCAG 1.3.1 / 2.4.3

   Broadcast theme has both a .header__mobile and
   .header__desktop in the DOM at all times.
   CSS hides one visually but screen readers still
   read both. This toggles aria-hidden so only the
   active nav is announced.
   ───────────────────────────────────────────── */
function initNavAriaHidden() {
  var mobileHeader = document.querySelector('.header__mobile');
  var desktopHeader = document.querySelector('.header__desktop');
  var body = document.body;

  if (!mobileHeader || !desktopHeader) return;

  function updateAriaHidden() {
    var isMobileLayout = body.classList.contains('js__show__mobile') ||
                         document.querySelector('[data-header-style="drawer"]') !== null;

    if (isMobileLayout) {
      mobileHeader.removeAttribute('aria-hidden');
      desktopHeader.setAttribute('aria-hidden', 'true');
      // Prevent keyboard focus into hidden desktop nav
      desktopHeader.querySelectorAll('a, button, input, select, [tabindex]').forEach(function(el) {
        el.setAttribute('tabindex', '-1');
      });
    } else {
      desktopHeader.removeAttribute('aria-hidden');
      mobileHeader.setAttribute('aria-hidden', 'true');
      // Restore desktop nav focusability; mobile handled by drawer open/close
      desktopHeader.querySelectorAll('[tabindex="-1"]').forEach(function(el) {
        el.removeAttribute('tabindex');
      });
    }
  }

  // Run on load and on resize
  updateAriaHidden();
  window.addEventListener('resize', updateAriaHidden);

  // Also watch for class changes on body (Broadcast adds js__show__mobile dynamically)
  var bodyObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(m) {
      if (m.attributeName === 'class') updateAriaHidden();
    });
  });
  bodyObserver.observe(body, { attributes: true });
}

// Run after existing init
document.addEventListener('DOMContentLoaded', initNavAriaHidden);
